// components/superAdmin/SuperAdminUsersTab.js
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import {
  createPlatformUser,
  deletePlatformUser,
  toggleUserActive,
  resetUserPassword,
} from '../../slices/superAdminSlice';
import { useToast } from '../../context/ToastContext';
import { SA, cardSx } from './theme';

const roleLabels = {
  manager: 'مدير',
  employee: 'موظف',
  teacher: 'معلم',
  parent: 'ولي أمر',
  student: 'طالب',
};

const SuperAdminUsersTab = ({ users, accounts, onRefresh }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '', password: '', email: '', role: 'manager',
    first_name: '', last_name: '', phone: '', account: '',
  });

  const handleToggleActive = async (user) => {
    try {
      await dispatch(toggleUserActive(user.id)).unwrap();
      showToast('تم تحديث حالة المستخدم بنجاح', 'success');
    } catch (error) {
      showToast(error?.error || 'حدث خطأ أثناء التحديث', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await dispatch(deletePlatformUser(deleteDialog.id)).unwrap();
      showToast('تم حذف المستخدم بنجاح', 'success');
      setDeleteDialog(null);
    } catch (error) {
      showToast(error?.error || 'حدث خطأ أثناء الحذف', 'error');
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog || newPassword.length < 6) {
      showToast('كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'error');
      return;
    }
    try {
      await dispatch(resetUserPassword({ id: resetDialog.id, new_password: newPassword })).unwrap();
      showToast('تم تغيير كلمة المرور بنجاح', 'success');
      setResetDialog(null);
      setNewPassword('');
    } catch (error) {
      showToast(error?.error || 'حدث خطأ أثناء تغيير كلمة المرور', 'error');
    }
  };

  const handleCreate = async () => {
    if (!createForm.username || !createForm.password || !createForm.account) {
      showToast('اسم المستخدم وكلمة المرور والحساب مطلوبة', 'error');
      return;
    }
    try {
      await dispatch(createPlatformUser(createForm)).unwrap();
      showToast('تم إنشاء المستخدم بنجاح', 'success');
      setCreateOpen(false);
      setCreateForm({ username: '', password: '', email: '', role: 'manager', first_name: '', last_name: '', phone: '', account: '' });
      onRefresh();
    } catch (error) {
      showToast(error?.username?.[0] || error?.account?.[0] || error?.detail || 'حدث خطأ أثناء إنشاء المستخدم', 'error');
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = !searchTerm ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesAccount = !accountFilter || String(user.account) === String(accountFilter);
    return matchesSearch && matchesRole && matchesAccount;
  });

  return (
    <Box>
      <Stack useFlexGap direction="row" spacing={1.5} mb={2} alignItems="center" flexWrap="wrap" sx={{ '& .MuiTextField-root': { bgcolor: '#fff' } }}>
        <TextField
          placeholder="ابحث باسم المستخدم، الاسم، البريد..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField
          select
          label="الصلاحية"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">الكل</MenuItem>
          {Object.entries(roleLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="المدرسة"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">الكل</MenuItem>
          {accounts.map((acc) => (
            <MenuItem key={acc.id} value={acc.id}>{acc.school_name || acc.name}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ color: SA.muted, fontSize: 13, fontFamily: SA.font }}>{filteredUsers.length} مستخدم</Typography>
        <Button startIcon={<AddIcon />} variant="contained" disableElevation onClick={() => setCreateOpen(true)} sx={{ borderRadius: 2, bgcolor: SA.primary }}>
          مستخدم جديد
        </Button>
      </Stack>

      <TableContainer sx={cardSx}>
        <Table size="small" sx={{ '& td, & th': { fontFamily: SA.font, borderColor: SA.border }, '& th': { color: SA.muted, fontWeight: 700, fontSize: 12.5, bgcolor: '#FAFBFD' }, '& td': { py: 1.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>اسم المستخدم</TableCell>
              <TableCell>الاسم الكامل</TableCell>
              <TableCell>البريد الإلكتروني</TableCell>
              <TableCell>المدرسة</TableCell>
              <TableCell>الصلاحية</TableCell>
              <TableCell>آخر تسجيل دخول</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: SA.muted }}>لا يوجد مستخدمون مطابقون</TableCell></TableRow>
            )}
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover sx={{ opacity: user.is_active ? 1 : 0.6 }}>
                <TableCell sx={{ fontWeight: 700, color: SA.text }}>
                  {user.username}
                  {user.is_superuser && <Chip label="مسؤول المنصة" size="small" color="secondary" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{user.full_name || '-'}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>{user.account_name || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={roleLabels[user.role] || user.role || '-'}
                    size="small"
                    color={user.role === 'manager' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{user.last_login ? new Date(user.last_login).toLocaleString('ar-EG') : '-'}</TableCell>
                <TableCell>
                  <Switch
                    checked={user.is_active}
                    onChange={() => handleToggleActive(user)}
                    disabled={user.is_superuser}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setResetDialog(user)}>
                    <LockResetIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialog(user)}
                    color="error"
                    disabled={user.is_superuser}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Critical action: delete confirmation */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف المستخدم <b>{deleteDialog?.username}</b>؟ لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained">حذف نهائياً</Button>
        </DialogActions>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetDialog} onClose={() => { setResetDialog(null); setNewPassword(''); }}>
        <DialogTitle>تغيير كلمة المرور — {resetDialog?.username}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="كلمة المرور الجديدة"
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetDialog(null); setNewPassword(''); }}>إلغاء</Button>
          <Button onClick={handleResetPassword} variant="contained">تغيير</Button>
        </DialogActions>
      </Dialog>

      {/* Create user dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إضافة مستخدم جديد</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select fullWidth label="المدرسة" required
              value={createForm.account}
              onChange={(e) => setCreateForm({ ...createForm, account: e.target.value })}
            >
              {accounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>{acc.school_name || acc.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth label="اسم المستخدم" required
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            />
            <TextField
              fullWidth label="كلمة المرور" required
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            />
            <TextField
              select fullWidth label="الصلاحية"
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            >
              {Object.entries(roleLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth label="الاسم الأول"
              value={createForm.first_name}
              onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
            />
            <TextField
              fullWidth label="الاسم الأخير"
              value={createForm.last_name}
              onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })}
            />
            <TextField
              fullWidth label="البريد الإلكتروني"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
            />
            <TextField
              fullWidth label="الهاتف"
              value={createForm.phone}
              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>إلغاء</Button>
          <Button onClick={handleCreate} variant="contained">إنشاء</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminUsersTab;
